import React, { useState, useRef, useEffect } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import { step1Questions, exemptionInitialQuestions, initialAdditionalQuestions } from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function LouisianaForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  getPdfUrl,
  getInitialQuestions,
  resetInitialQuestions,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  const formatFirstNameMiddleInitial = () => {
    const firstName = `${`${prefilledData.employee_first_name} `}` || '';
    const middleInitial = prefilledData.employee_middle_name || '';
    return firstName + middleInitial;
  };
  prefilledData.employee_first_name_middle_initial = formatFirstNameMiddleInitial();

  const step1Form = useRef();
  const step1InitialQuestions = getInitialQuestions(step1Questions);
  const [stepOneQuestions, setStepOneQuestions] = useState(step1InitialQuestions);

  const exemptionForm = useRef();
  const exemptionQuestionsWithAnswers = getInitialQuestions(exemptionInitialQuestions);
  const [exemptionQuestions, setExemptionQuestions] = useState(exemptionQuestionsWithAnswers);

  const additionalForm = useRef();
  const additionalQuestionsWithAnswers = getInitialQuestions(initialAdditionalQuestions, '0');
  const [additionalQuestions, setAdditionalQuestions] = useState(additionalQuestionsWithAnswers);

  // to calculate total number of exemptions
  const choice = exemptionQuestions[0].answer.answer.filing_status;
  const calculateTotalExemptions = () => {
    let value = 0;
    switch (choice) {
      case 'single':
        value += 1;
        break;
      case 'married':
        value += 2;
        break;
      default:
        break;
    }

    return value;
  };

  useEffect(() => {
    const resettedInitialQuestions = resetInitialQuestions(initialAdditionalQuestions, '0');
    if (choice === 'no_exemptions_or_dependents_claimed') {
      setAdditionalQuestions(resettedInitialQuestions.map((q) => {
        q.locked = true;
        return { ...q };
      }));
    } else if (
      choice !== 'no_exemptions_or_dependents_claimed'
      && additionalQuestions[0].answer.answer.dependents === '0'
      && additionalQuestions[1].answer.answer.additional_withholding === '0'
    ) {
      setAdditionalQuestions(resettedInitialQuestions);
    }
  }, [choice]);

  const renderStep1Form = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={stepOneQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOneQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneQuestions(questions);
        }}
        ref={step1Form}
      />
      <Text style={{ fontSize: '14px', fontWeight: '600' }} mb="15px">
        Exemption from withholding
      </Text>
      <FormConsumer
        questions={exemptionQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = exemptionQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setExemptionQuestions(questions);
        }}
        ref={exemptionForm}
      />
      <S.QuestionWrapper mb="8px">
        <Flex mb="7px">
          <S.QuestionLabel>
            Total number of exemptions:
          </S.QuestionLabel>
        </Flex>
        <Flex mb="10px">
          <S.Input
            type="text"
            value={calculateTotalExemptions()}
            disabled
          />
        </Flex>
      </S.QuestionWrapper>
      <FormConsumer
        questions={additionalQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = additionalQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setAdditionalQuestions(questions);
        }}
        ref={additionalForm}
      />
    </div>
  );

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderStep1Form()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;
            if (step1Form && step1Form.current) {
              step1Form.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (exemptionForm && exemptionForm.current) {
              exemptionForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (additionalForm && additionalForm.current) {
              additionalForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (err) {
              return;
            }
            // No error, proceed to save
            // - save answers to server
            // - processed to next step
            const stepOneData = stepOneQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            const exemptionData = exemptionQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            const additionalData = additionalQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            const totalExemptionsData = { total_number_of_exemptions: calculateTotalExemptions().toString() };
            const data = {
              ...stepOneData, ...exemptionData, ...additionalData, ...totalExemptionsData,
            };
            saveFormData(data, () => {
              setCurrentStep(STEPS.step2);
              scrollTop();
            });
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderStep1Form(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default LouisianaForm;
